import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { apiClient } from '../services/api.client';

export type Language = 'en' | 'es' | 'fr' | 'de';

interface Translation {
  [key: string]: string | Translation;
}

interface ApiTranslation {
  page: string;
  section: string;
  element: string;
  textEN: string;
  textFR: string;
  // Add other languages if supported by API
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string>) => string;
  availableLanguages: Language[];
  isLoading: boolean;
  loadPageTranslations: (page: string) => Promise<void>;
  translations: Record<Language, Translation>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
  defaultLanguage?: Language;
}

// Empty translations (API will populate these)
const emptyTranslations: Record<Language, Translation> = {
  en: {},
  es: {},
  fr: {},
  de: {},
};

// Cache version - increment to invalidate old cached translations
const TRANSLATIONS_CACHE_VERSION = 4;

// Detect initial language synchronously (before first render)
const getInitialLanguage = (fallback: Language): Language => {
  if (typeof window === 'undefined') return fallback;

  // Check localStorage first
  const savedLang = localStorage.getItem('language') as Language | null;
  if (savedLang && ['en', 'es', 'fr', 'de'].includes(savedLang)) {
    return savedLang;
  }

  // Detect browser language
  const browserLang = navigator.language.split('-')[0];
  if (browserLang && ['en', 'es', 'fr', 'de'].includes(browserLang)) {
    return browserLang as Language;
  }

  return fallback;
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
  defaultLanguage = 'en',
}) => {
  // Initialize with correct language immediately - no flash
  const [language, setLanguageState] = useState<Language>(() =>
    getInitialLanguage(defaultLanguage)
  );

  // Get cached translations from localStorage (prevents flash on reload)
  const getCachedTranslations = (): Record<Language, Translation> | null => {
    try {
      const cachedVersion = localStorage.getItem('translations_cache_version');
      // Invalidate cache if version mismatch
      if (cachedVersion !== String(TRANSLATIONS_CACHE_VERSION)) {
        localStorage.removeItem('translations_cache');
        return null;
      }
      const cached = localStorage.getItem('translations_cache');
      if (cached) {
        return JSON.parse(cached);
      }
    } catch {
      // Ignore cache errors
    }
    return null;
  };

  const cachedTranslations = getCachedTranslations();
  const [translations, setTranslations] = useState<
    Record<Language, Translation>
  >(cachedTranslations || emptyTranslations);
  const [isLoading, setIsLoading] = useState(!cachedTranslations);

  // Fetch translations from API
  useEffect(() => {
    const fetchTranslations = async () => {
      try {
        const response = await apiClient.get<{ data: ApiTranslation[] }>(
          '/public/translations'
        );
        const apiTranslations = response.data.data;

        // Deep merge logic or simple structure building
        const newTranslations = JSON.parse(JSON.stringify(emptyTranslations));

        apiTranslations.forEach((t) => {
          // Helper to ensure nested object exists and set value at the end
          const setNestedValue = (
            obj: Translation,
            path: string[],
            value: string
          ) => {
            let current: Translation = obj;
            for (let i = 0; i < path.length - 1; i++) {
              const key = path[i];
              if (!key) continue;
              if (!current[key] || typeof current[key] === 'string') {
                current[key] = {};
              }
              current = current[key] as Translation;
            }
            const lastKey = path[path.length - 1];
            if (lastKey) {
              current[lastKey] = value;
            }
          };

          // Build full path: page.section.element (element can contain dots)
          const elementParts = t.element.split('.');
          const fullPath = [t.page, t.section, ...elementParts];

          // Map to EN
          if (t.textEN) {
            setNestedValue(newTranslations.en, fullPath, t.textEN);
          }

          // Map to FR
          if (t.textFR) {
            setNestedValue(newTranslations.fr, fullPath, t.textFR);
          }

          // Add other languages mapping here if available in API
        });

        setTranslations(newTranslations);

        // Cache translations for next load (prevents flash)
        try {
          localStorage.setItem(
            'translations_cache',
            JSON.stringify(newTranslations)
          );
          localStorage.setItem(
            'translations_cache_version',
            String(TRANSLATIONS_CACHE_VERSION)
          );
        } catch {
          // Ignore cache errors
        }
      } catch (error) {
        console.error('Failed to fetch translations:', error);
        // Keep default translations on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchTranslations();
  }, []);

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string>): string => {
      const keys = key.split('.');
      let value: string | Translation = translations[language];

      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          const nextValue: string | Translation | undefined = (
            value as Translation
          )[k];
          if (nextValue !== undefined) {
            value = nextValue;
          } else {
            // console.warn(`Translation key not found: ${key}`);
            return key;
          }
        } else {
          // console.warn(`Translation key not found: ${key}`);
          return key;
        }
      }

      if (typeof value !== 'string') {
        // console.warn(`Translation value is not a string: ${key}`);
        return key;
      }

      // Replace parameters
      let translatedValue = value;
      if (params) {
        Object.entries(params).forEach(([param, val]) => {
          translatedValue = translatedValue.replace(
            new RegExp(`{{${param}}}`, 'g'),
            val
          );
        });
      }

      return translatedValue;
    },
    [language, translations]
  );

  const loadPageTranslations = useCallback(async (page: string) => {
    try {
      const response = await apiClient.get<{ data: ApiTranslation[] }>(
        `/translations/page/${page}`
      );
      const pageTranslations = response.data.data;

      setTranslations((prev) => {
        const newTranslations = JSON.parse(JSON.stringify(prev));

        pageTranslations.forEach((t) => {
          // Helper to ensure nested object exists and set value at the end
          const setNestedValue = (
            obj: Translation,
            path: string[],
            value: string
          ) => {
            let current: Translation = obj;
            for (let i = 0; i < path.length - 1; i++) {
              const key = path[i];
              if (!key) continue;
              if (!current[key] || typeof current[key] === 'string') {
                current[key] = {};
              }
              current = current[key] as Translation;
            }
            const lastKey = path[path.length - 1];
            if (lastKey) {
              current[lastKey] = value;
            }
          };

          // Build full path: page.section.element (element can contain dots)
          const elementParts = t.element.split('.');
          const fullPath = [t.page, t.section, ...elementParts];

          // Map to EN
          if (t.textEN) {
            setNestedValue(newTranslations.en, fullPath, t.textEN);
          }

          // Map to FR
          if (t.textFR) {
            setNestedValue(newTranslations.fr, fullPath, t.textFR);
          }
        });

        return newTranslations;
      });
    } catch (error) {
      console.error(`Failed to fetch translations for page ${page}:`, error);
    }
  }, []);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
    availableLanguages: ['en', 'es', 'fr', 'de'],
    isLoading,
    loadPageTranslations,
    translations, // Expose raw translations
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
